/**
 * 批量导入面板：**粘贴 → 预览 → 提交**。
 *
 * ## 为什么这样组织
 *
 * 1. **三步在一屏上从上到下走**，没有向导、没有弹窗：家长粘的是几千个字，
 *    在任何一步丢一次输入都要重来。三步的输入框与结果都在同一屏上。
 * 2. **预览与提交是同一个 `state`**：状态机在 `lib/dictationImport.ts`
 *    （纯函数、有单测；请求体由那里的 `buildImportPayload` 构造）。提交用的是
 *    **预览那一份 payload**，不从输入重算；输入一改签名就变，提交按钮立刻禁用并说明为什么。
 * 3. **格式说明就在界面上**（```<details>``` 折叠）：三种贴法 + 列序 + 表头别名
 *    都是从 `packages/shared/src/dictation/import-parser.ts` 的**真实实现**里
 *    抄过来的，家长不需要去翻文档。
 * 4. 逐行错误**按行号列出原文与原因**（后端给的 `line` 是原文行号，从 1 开始）。
 *    `line = 0` 的是"整份文本级"提示（例如"有 1 行重复已自动合并"），
 *    与"第 0 行"是两回事，所以分两组渲染。
 */
import { useMemo } from 'react';
import { Button } from '../ui/Button';
import { Badge, Card, CardHeader } from '../ui/Card';
import { Select } from '../ui/Input';
import { IconCheck, IconChevronDown, IconUpload } from '../ui/Icons';
import {
  GRADES,
  describeDistinctTotal,
  describePolyphonicCount,
  describePolyphonicDuplicates,
  describeTextbookLabel,
  gradeLabel,
  publisherDisplayNameOf,
} from '@fht/shared';
import type { ImportDuplicateItem } from '@fht/shared';
import { cn } from '../../lib/utils';
import { useImportCounter } from '../../lib/useImportCounter';
import { countLabel, semesterLabel } from '../../lib/api/dictation';
import type { ImportReportView, ImportCountsView, TextbookView } from '../../lib/api/dictation';
import {
  IMPORT_MAX_ROWS,
  IMPORT_TEXT_MAX,
  canCommitImport,
  describePreviewForConfirm,
  describeTruncation,
  isImportDirty,
  splitImportIssues,
  validateImportInput,
} from '../../lib/dictationImport';
import type { ImportState } from '../../lib/dictationImport';
import { ImportIssueList as IssueList } from './ImportIssueList';
import { DuplicateReport } from './DuplicateReport';

const SEMESTER_OPTIONS = ['上', '下', '不分'] as const;

/** 一行统计：数字拿不到时显示「—」，不显示 0。 */
function Stat({ label, value, tone = 'slate' }: { label: string; value: number | null; tone?: 'slate' | 'emerald' | 'amber' | 'rose' }) {
  const tones = {
    slate: 'bg-slate-100 text-slate-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    amber: 'bg-amber-50 text-amber-700',
    rose: 'bg-rose-50 text-rose-700',
  } as const;
  return (
    <div className={cn('rounded-xl px-3 py-2', tones[tone])}>
      <p className="text-xs opacity-80">{label}</p>
      <p className="text-lg font-bold tabular-nums">{countLabel(value)}</p>
    </div>
  );
}

/**
 * 预览里那张**能自己加得起来的账**（2026-10-16 用户明确要求）。
 *
 * 用户原话：
 *
 * > "你保存之前……要**统计有多少个字或者多少个词语**，那我会核对嘛。比如说我就录了
 * >  100 个字，结果呢你检测之后呢只有 99 或者 101 这样的，那就跟我录的不一样……
 * >  **起码你数字要跟我录的核对之后对得上，我才会继续点保存**"
 *
 * 所以这一块的每一行都必须是**从后端那一份账里读出来的**（不在这里另算）：
 *
 *    你在框里粘了：            100 个字元
 *      识别出来：              100 个（字 100 / 词 0）
 *      　├ 本次新增：           10 个
 *      　├ 之前已录过（会跳过）： 88 个
 *      　├ 会更新（拼音/收录/词语表）： 0 个
 *      　├ 份内重复·打错的（已合并）： 2 个
 *      　├ 多音字重复（只存 1 条）：   0 个
 *      　└ 需要补拼音（这次没收）： 0 个
 *    有问题、没收的：             0 个
 *    一共 98 个字（多音字只算一次）      ← 用户点名要的数（2026-10-17）
 *    多音字（重复出现的）：0 个
 *
 * ⚠️ 最后两行不参与上面那条加法：它们回答的是"**实际会留在字表里几行**"
 *    （重复的只算 1 次）与"**多音字有几个**"（还要把读音列出来）。
 * ⚠️ 手机可读：用 `flex flex-wrap`（窄屏自动折行）+ `tabular-nums`，
 *    而不是一张 `<table>`（窄屏上会挤成一团）。用户主要在手机上看这一屏。
 */
function ImportAccount({
  counts,
  polyphonic,
}: {
  counts: ImportCountsView;
  /** 多音字重复的明细（`report.duplicates.polyphonic`；`null` = 这块没拿到）。 */
  polyphonic: readonly ImportDuplicateItem[] | null;
}) {
  /** 一行"标签 —— 数字"。标签在窄屏上先让位（`min-w-0`），数字不缩（`shrink-0`）。 */
  const Line = ({
    label,
    value,
    strong = false,
    tone = 'slate',
  }: {
    label: string;
    value: number;
    strong?: boolean;
    tone?: 'slate' | 'emerald' | 'amber' | 'rose';
  }) => (
    <li className="flex min-w-0 items-baseline gap-2">
      <span className={cn('min-w-0 flex-1', strong ? 'font-semibold text-slate-800' : 'text-slate-600')}>
        {label}
      </span>
      <span
        className={cn(
          'shrink-0 font-mono tabular-nums',
          strong && 'font-semibold',
          tone === 'emerald' && 'text-emerald-700',
          tone === 'amber' && 'text-amber-700',
          tone === 'rose' && 'text-rose-700',
          tone === 'slate' && 'text-slate-800',
        )}
      >
        {value}
      </span>
    </li>
  );
  const listTypeLabel =
    counts.listType === 'shizi'
      ? '识字表（只统计字）'
      : counts.listType === 'xiezi'
        ? '写字表（只统计字）'
        : counts.listType === 'cibiao'
          ? '词语表（只统计词语）'
          : '通用导入（字与词分开算）';
  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-white px-3 py-2">
      <p className="text-sm font-semibold text-slate-800">
        这次录的是：{listTypeLabel}
      </p>
      <ul className="mt-1 space-y-0.5 text-sm">
        <Line label="你在框里粘了（字元）" value={counts.tokens} strong />
        <Line label="识别出来（字 + 词）" value={counts.recognized} strong />
        <Line label={`　其中：字 ${counts.charRows} 个 / 词 ${counts.wordRows} 个`} value={counts.charRows + counts.wordRows} />
        <Line label="　├ 本次新增" value={counts.created} tone="emerald" />
        <Line label="　├ 之前已录过（会跳过）" value={counts.skipped} />
        <Line label="　├ 会更新（拼音/收录/词语表）" value={counts.updated} tone="amber" />
        {/*
          ⚠️ 2026-10-17：原来只有一行「同一份里重复（已合并）」，把"打错了"与
             "这个字本来就有多个读音"混在一起 —— 用户明确说**多音字不能当重复处理**。
             现在两档各占一行，且都在这条加法链里（见 `summarizeImportCounts`）。
        */}
        <Line label="　├ 份内重复·打错的（已合并）" value={counts.merged} tone="amber" />
        <Line label="　├ 多音字重复（只存 1 条）" value={counts.mergedPolyphonic} />
        <Line label="　└ 需要补拼音（这次没收）" value={counts.unresolved} tone="rose" />
        <Line label="有问题、没收的（逐条列在下面）" value={counts.problem} tone="rose" strong />
      </ul>

      {/*
        ---- 用户点名要的两个数（"在预览里显眼位置"）----
        · 一共几个字（多音字只算一次）= 实际会留在字表里的行数口径；
        · 多音字有几个 + 把它们（连读音）列出来。
        ⚠️ 文案走 shared 的 `describeDistinctTotal` / `describePolyphonicCount`
           （有单测），这里只排版，不自己拼数字。
      */}
      <p className="mt-2 rounded-lg bg-slate-100 px-2 py-1 text-sm font-semibold text-slate-800">
        {describeDistinctTotal(counts)}
      </p>
      <p className="mt-1 text-xs text-slate-600">
        {describePolyphonicCount(counts)}
        {counts.polyphonicChars > 0 && (
          <>
            {' —— '}
            <span className="font-mono text-slate-800">
              {polyphonic === null
                ? '明细没拿到'
                : describePolyphonicDuplicates(polyphonic) || '明细没拿到'}
            </span>
          </>
        )}
      </p>
      {/*
        ⚠️ 恒等式不成立时**明说**它是 bug（而不是糊一句"数字可能有误"）：
           `字元 = 识别出来 + 有问题`、`识别出来 = 新增+跳过+更新+份内重复+需补拼音`。
      */}
      {!counts.balanced && (
        <p className="mt-2 rounded-lg border border-rose-200 bg-rose-50 px-2 py-1 text-xs text-rose-700">
          这张账<strong>对不上</strong>（{counts.tokens} ≠ {counts.recognized} + {counts.problem}，或
          识别出来的数不等于下面几项之和）—— 这是我们算错了，请把这一屏截图发我，
          <strong>先别提交</strong>。
        </p>
      )}
      <p className="mt-1 text-xs text-slate-500">
        核对方式：<code>字元</code> = <code>识别出来</code> + <code>有问题</code>；
        <code>识别出来</code> = 新增 + 已录过 + 会更新 + 份内重复·打错的 + 多音字重复 + 需补拼音。
        <br />
        <code>一共几个字</code> = 新增 + 已录过 + 会更新 + 需补拼音（同一份里重复的只算 1 次，
        多音字也只算 1 次）。
      </p>
    </div>
  );
}

export interface ImportPanelProps {
  state: ImportState;
  textbooks: TextbookView[] | null;
  busy: boolean;
  onInput: (patch: Partial<ImportState['input']>) => void;
  onPreview: () => void;
  onCommit: () => void;
  onReset: () => void;
  onRestoreLastText: () => void;
}

export function DictationImportPanel({
  state,
  textbooks,
  busy,
  onInput,
  onPreview,
  onCommit,
  onReset,
  onRestoreLastText,
}: ImportPanelProps) {
  const gate = canCommitImport(state);
  const dirty = isImportDirty(state);
  const previewReport = state.preview.report;
  const commitReport = state.commit.report;
  const report = commitReport ?? previewReport;

  /**
   * 框右下角那个计数器（`lib/importCounter.ts`，纯函数 + 单测）。
   *
   * ⚠️ 2026-10-17 修的就是它：改造前显示 `text.length`（含空格换行），
   *    用户那段 33 行 / 284 个汉字的内容显示成 568 个字符，与预览的「生字 284 条」
   *    对不上。现在前面那个数与预览同口径（`stats.recognized`），后面那个是字符数。
   *
   * ⚠️ **复用预览那一份**：同一份文本已经预览过时不再解析
   *    （`previewPayload.text` 就是预览时真正发出去的那一份 —— 它相等才说明
   *    "预览的就是现在框里这堆字"；改过一个字就不复用）。
   * ⚠️ 通用整块导入**没有表类型**，所以用中性措辞「已识别 N 个条目」——
   *    这里**不猜**它是字表还是词语表（猜错会让人以为程序数错了）。
   */
  const counterReport =
    state.previewPayload !== null &&
    state.previewPayload.text === state.input.text &&
    state.preview.report !== null
      ? state.preview.report
      : null;
  const counter = useImportCounter({
    text: state.input.text,
    listType: null,
    defaultTextbook: state.input.textbook,
    limit: IMPORT_TEXT_MAX,
    ...(counterReport?.summary.counts !== null && counterReport?.summary.counts !== undefined
      ? { reportedUnits: counterReport.summary.counts.recognized }
      : {}),
  });

  /**
   * 本地校验（**点预览之前**就把"缺年级/学期"这类问题说出来）。
   *
   * ⚠️ 只用来给提示，**不**用来禁用按钮：禁用一个说不出原因的按钮是本项目
   *    明确反对的形态。按钮照常能点，点了会看到下面那行红字。
   */
  const localProblem = report === null ? validateImportInput(state.input) : null;

  const issueGroups = useMemo(() => {
    if (report === null) return null;
    return {
      errors: splitImportIssues(report.errors),
      warnings: splitImportIssues(report.warnings),
    };
  }, [report]);

  return (
    <div className="space-y-4">
      {/* ============================================ 第 1 步：粘贴 */}
      <Card>
        <CardHeader
          title="① 粘贴字表 / 词语表"
          description="课本后面的 1 类字、2 类字、词语表都可以整块粘进来。支持从 Excel/表格里直接复制。"
        />

        {/*
          ⚠️ 这个入口（通用整块导入）与「按书录入」的差别必须说清，否则家长会
             把这里"一行里字 + 词都写"的写法抄到「按书录入」里，然后被报错。
        */}
        <p className="mb-3 rounded-xl bg-slate-50 px-3 py-2 text-xs text-slate-600">
          这一屏是<strong>通用整块导入</strong>：`字` 与 `词` 是<strong>两列</strong>，
          所以一行里"字 + 由它组成的词"都写是允许的（老写法的兼容口径）。
          <br />
          ⚠️ 而「<strong>按书录入</strong>」是按<strong>一张表</strong>录：
          识字表/写字表里每个词元只能是一个汉字、词语表里每个词元只能是词语或成语
          —— 那里的规则更严，见下面那段说明。
        </p>

        <label htmlFor="dictation-import-text" className="mb-1 block text-sm font-medium text-slate-700">
          内容
        </label>
        <textarea
          id="dictation-import-text"
          value={state.input.text}
          onChange={(event) => onInput({ text: event.target.value })}
          spellCheck={false}
          rows={12}
          placeholder={
            '把课本内容整块粘在这里。三种最常用的贴法都能直接用：\n\n' +
            '① 表格（从 Excel 复制，制表符分隔）\n' +
            '课本\t年级\t学期\t课文\t字\t词\t拼音\t类别\n' +
            '人民教育出版社\t一年级\t上册\t第1课 秋天\t秋\t\tqiū\t1类字\n' +
            '人民教育出版社\t一年级\t上册\t第1课 秋天\t\t秋天\tqiū tiān\t词语\n\n' +
            '② 按课本目录（下面两行会继承上面的课本/年级/学期）\n' +
            '课本\t年级\t学期\t课文\n' +
            '人民教育出版社\t一年级\t上册\t第1课 秋天\n' +
            '\t\t\t秋 气 了 子 人 大\n' +
            '秋天 天气 叶子 大人\n\n' +
            '③ 一行一个词（课本/年级/学期用右边那几格）\n' +
            '秋天\n' +
            '天气'
          }
          className={cn(
            'w-full rounded-xl border border-slate-300 bg-white px-3 py-2 font-mono text-sm leading-6 text-slate-900',
            'placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100',
            // 手机上要够大：至少 12 行 + 一个较大的最小高度（滚动页而不是挤压页面）
            'min-h-[16rem] sm:min-h-[20rem]',
          )}
        />
        <div className="mt-1 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
          {/*
            ⚠️ 这一行是 2026-10-17 修的"字数统计"：前面是**识别出的条目数**
               （与预览里「生字 N 条」同口径），后面是**字符数**（含空格/换行）。
               文案由 `describeImportCounter()` 统一生成（有单测），这里不自己拼。
          */}
          <span>{counter.label}</span>
          {state.lastCommittedText !== null && state.input.text !== state.lastCommittedText && (
            <button
              type="button"
              onClick={onRestoreLastText}
              className="font-medium text-brand-600 hover:underline"
            >
              填回上次导入的内容（接着往里加）
            </button>
          )}
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Select
            label="默认课本"
            value={state.input.textbook}
            onChange={(event) => onInput({ textbook: event.target.value })}
            hint="文本里没写「课本」列时用它"
          >
            <option value="">不指定（每一行自己写「课本」列）</option>
            {(textbooks ?? []).map((textbook) => (
              /*
                ⚠️ 选项文字**必须带年级 + 学期**（`describeTextbookLabel`）：
                   一本书就是一册，只显示出版社时上册与下册长得一模一样。
                   ⛔ 不显示版次（2026-10-18 起彻底去掉）。
              */
              /*
                ⚠️ `value` 必须是**人话**（`publisherDisplayNameOf`）：这个值是作为
                   「课本」引用发给后端的。历史行 `publisher` 是 `pub_xxxxxxxx`，
                   把代号发过去等于让后端拿一个不可逆的哈希当出版社 ——
                   2026-10-18 起后端会直接 400（"请写出版社名称"）。
              */
              <option key={textbook.id} value={publisherDisplayNameOf(textbook)}>
                {describeTextbookLabel(textbook)}
              </option>
            ))}
          </Select>
          <Select
            label="默认年级"
            value={state.input.gradeCode}
            onChange={(event) => onInput({ gradeCode: event.target.value })}
            hint="文本里没写「年级」列时用它（课本按册建）"
          >
            <option value="">不指定（课文行必须自带年级）</option>
            {GRADES.map((grade) => (
              <option key={grade.code} value={grade.code}>
                {gradeLabel(grade.code)}
              </option>
            ))}
          </Select>
          <Select
            label="默认学期"
            value={state.input.semester}
            onChange={(event) => onInput({ semester: event.target.value })}
            hint="课本按册建：一年级上册和一年级下册是两本书"
          >
            <option value="">不指定</option>
            {SEMESTER_OPTIONS.map((semester) => (
              <option key={semester} value={semester}>
                {semesterLabel(semester)}
              </option>
            ))}
          </Select>
        </div>

        {/*
          格式说明**就在界面上**（不用去翻文档）。内容逐条对着
          `packages/shared/src/dictation/import-parser.ts` 的实现写。
        */}
        <details className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
          <summary className="flex cursor-pointer items-center gap-1 text-sm font-medium text-slate-700">
            <IconChevronDown size={16} />
            导入格式说明（支持的分隔符 / 列序 / 表头别名 / 三种贴法）
          </summary>
          <div className="mt-3 space-y-3 text-sm text-slate-600">
            {/*
              ⚠️ 「一本书 = 一册」是这个面板上最重要的一条规则（2026-10-18 定稿），
                 所以放在说明块**最前面**，用醒目的底色，而不是埋在别名列表里。
                 ⛔ 版次已经彻底不用：文本里即使还有「版次」列，也会被忽略、不报错。
            */}
            <p className="rounded-lg bg-brand-50 px-3 py-2 text-brand-900">
              <strong>一本书 = 一个具体的册</strong>（出版社 + <strong>年级</strong> +{' '}
              <strong>学期</strong>）：一年级上册和一年级下册
              <strong>是两本书，分别建</strong>。
              文本里没写「年级」「学期」列时，用右边那两格「默认年级 / 默认学期」；
              两处都没有 → <strong>这一行会报错</strong>（不会猜一个册别）。
              <br />
              ⛔ <strong>「版次」已经不需要填了</strong>：文本里即使还有「版次」列，
              也会被忽略、不报错（旧文本照原样粘就行）。
            </p>
            {/*
              ⚠️ 三张表各自的**准入规则**（2026-10-16 用户明确要求）：
                  识字表/写字表只能录单个汉字、词语表只能录词语或成语。
                  这条规则是**逐词元**判的，所以这段话必须说清"是哪一个"会被拒。
            */}
            <p className="rounded-lg bg-rose-50 px-3 py-2 text-rose-900">
              <strong>三张表各自只能录什么</strong>（按<b>每一个词元</b>判，不是按行）：
              <br />
              · <strong>识字表 / 写字表</strong>：每一个词元必须是<strong>恰好一个汉字</strong>。
              一行可以写任意多个，用<strong>空格或 Tab</strong> 分开
              （<code>天 地 人</code> / <code>口&#9;耳&#9;目</code> / 一行一个都行）。
              <br />
              · <strong>词语表</strong>：每一个词元必须是<strong>词语或成语（2 个及以上汉字）</strong>
              （<code>天地 人口 耳目</code> / <code>画蛇添足</code>）。单个汉字会被拒。
              <br />
              · 写错的词元会<strong>逐条报出来（带行号 + 哪个词元 + 原因）</strong>，
              那一个词元不导入，同一行里合法的照常导入。
              <br />
              · ⚠️ <strong>给某个字造词/成语</strong>：保存后在「浏览与编辑」里
              <strong>点那个字</strong>，在弹出框里录（导入里不再支持"顺手写词"）。
            </p>
            <p>
              <strong className="text-slate-800">分隔符</strong>：制表符（从 Excel
              复制出来就是它）、逗号（中英文都认）、竖线 <code>|</code>、顿号 <code>、</code>、
              以及<strong>空格</strong>（连续多个算一个）。<strong>按表录入</strong>时
              空格与 Tab <strong>混用也认</strong>（<code>天&#9;地 人</code> 是三个字元），
              行首行尾空白与全角空格都会被吃掉。每行各算各的。
            </p>
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-amber-800">
              ⚠️ 一行里用<strong>单个空格</strong>分开多个词时（<b>通用整块导入</b>），无法区分
              &ldquo;两个词&rdquo;与&ldquo;课题 + 一个词&rdquo;，程序按前者处理
              （宁可多出两行词，也不悄悄丢数据）。要做<strong>列式粘贴请用制表符</strong>。
            </p>
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-amber-800">
              ⚠️ 判断&ldquo;这一行是不是表名行&rdquo;只看<strong>制表符 / 竖线</strong>
              分出来的第一格 —— 而逗号分隔的整行算一格，
              这一格里只要出现&ldquo;识字表 / 写字表 / 词语表&rdquo;，整行就会被当成<strong>表名行</strong>，
              后面的内容会按<strong>该表的词元</strong>逐个处理（不合适的会逐条报出来）。
              凡是要写<strong>「表」这一列</strong>，建议用<strong>制表符</strong>分列
              （从 Excel 复制出来就是它）。
            </p>
            <div>
              <p className="font-medium text-slate-800">没有表头时的列序（多余的列忽略）</p>
              <p className="mt-1 font-mono text-xs text-slate-700">
                课文 | 字 | 词 | 拼音 | 类别 | 课本 | 年级 | 学期 | 词语表
              </p>
              <p className="mt-1 text-xs text-slate-500">
                ⚠️ 如果旧文本里还有「版次」列，它会被<strong>忽略</strong>（不报错）。
                要写「表」这一列时请用制表符，见下面那条提示。
              </p>
            </div>
            <div>
              <p className="font-medium text-slate-800">有表头时按表头名对齐（顺序随便、列可以少）</p>
              <ul className="mt-1 space-y-0.5 text-xs">
                <li>
                  <strong>课文</strong>：课文 / 课题 / 课 / lesson（值可以是
                  <code>第5课 秋天</code>、<code>5 秋天</code>、<code>秋天</code>）
                </li>
                <li>
                  <strong>课序 / 单元</strong>：课序 / 序号 / no；单元 / 单元号 / unit
                </li>
                <li>
                  <strong>字</strong>：字 / 生字 / 汉字 / 会写字 / 会认字 / char
                </li>
                <li>
                  <strong>词</strong>：词 / 词语 / 组词 / word
                </li>
                <li>
                  <strong>拼音</strong>：拼音 / 读音 / pinyin（<strong>留空 = 自动生成</strong>，
                  要写声调符号：<code>qiū tiān</code>，不要写成 <code>qiu1 tian1</code>）
                </li>
                <li>
                  <strong>类别</strong>：1类字 / 2类字 / 会写字 / 会认字；常用词 / 词语 / 成语
                </li>
                <li>
                  <strong>课本</strong>：课本 / 教材 / book / textbook（可以是名字）
                </li>
                <li>
                  <strong>年级 / 学期</strong>：年级 / grade（<code>三年级</code> 或{' '}
                  <code>primary_3</code>）；学期 / 册 / semester（<code>上册</code> /{' '}
                  <code>上</code> / <code>下</code> / <code>不分</code>）——{' '}
                  <strong>课本按册建，这两列决定是哪一册</strong>
                </li>
                <li>
                  <strong>版次（已不用）</strong>：版次 / edition / 出版版次 / 印刷版次 /
                  第几版 —— <strong>认得但忽略、不报错</strong>。
                  ⚠️ 别名 <code>版本</code> 仍然指<strong>课本</strong>（老文本的兼容口径）。
                </li>
                <li>
                  <strong>年级 / 学期</strong>：年级 / grade（<code>三年级</code> 或{' '}
                  <code>primary_3</code>）；学期 / 册 / semester（<code>上册</code> /{' '}
                  <code>上</code> / <code>下</code> / <code>不分</code>）
                </li>
                <li>
                  <strong>词语表</strong>：词语表 / 是否词语表 / 来自词语表（
                  <code>是/否</code>、<code>1/0</code>、<code>√/×</code>）
                </li>
              </ul>
            </div>
            <div>
              <p className="font-medium text-slate-800">三种最常用的贴法</p>
              <pre className="mt-1 overflow-x-auto rounded-lg bg-white px-3 py-2 text-xs leading-5 text-slate-700">
{`① 表格（制表符，带表头）
课本	年级	学期	课文	字	词	拼音	类别
人民教育出版社	一年级	上册	第1课 秋天	秋			qiū	1类字
人民教育出版社	一年级	上册	第1课 秋天		秋天	qiū tiān	词语

② 按课本目录（课文行 + 生字串 + 词表行）
课本	年级	学期	课文
人民教育出版社	一年级	上册	第1课 秋天
秋 气 了 子 人 大
秋天 天气 叶子 大人
人民教育出版社	一年级	上册	第2课 小小的船
船 月 儿 头 里 坐

③ 一行一个词（单列；课本/年级/学期用上面那几格）
秋天
天气
小小的船`}
              </pre>
            </div>
            <p>
              <strong className="text-slate-800">重复导入不会产生重复数据</strong>
              ：同一本书（出版社 + **年级 + 学期**）里，一个字只存一行、一个词只存一行；
              已经存在的行会被跳过，只有分类 / 词语表 / 年级 / 学期 / 拼音这些属性有差异时才会更新。
              一年级上册和一年级下册是<strong>两本书</strong>，各录各的。
            </p>
            <p>
              <strong className="text-slate-800">一行写错不会让整批失败</strong>
              ：有问题的行会被单独列出来（带行号与原文），其余的行照常导入。
            </p>
          </div>
        </details>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button
            loading={state.preview.pending}
            disabled={busy && !state.preview.pending}
            onClick={onPreview}
            icon={<IconUpload size={18} />}
          >
            ② 预览（不会写入任何数据）
          </Button>
          {(state.input.text !== '' || report !== null) && (
            <Button variant="secondary" onClick={onReset}>
              清空重来
            </Button>
          )}
        </div>
        {/*
          ⚠️ 本地校验的提示（"缺年级/学期"这类）：**不禁用按钮**，而是点之前/之后就把它说清楚。
             本项目反对"点了没反应 / 灰着不说话"的按钮。
        */}
        {localProblem !== null && state.input.text.trim() !== '' && (
          <p className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            {localProblem}
          </p>
        )}
        {state.preview.error !== null && (
          <p className="mt-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            预览失败：{state.preview.error}
          </p>
        )}
        {/*
          ⚠️ **失败时的逐条原因**也要显示（用户实测："报错太笼统，看不出哪一行、
             哪个词元有问题"）。后端把真正的原因放在 `error.details` 里
             （`{field,reason}` / zod 的 `[{path,message}]` / 导入的
             `[{line,raw,token,message}]`），改造前界面只读 message 那一句通用文案。
        */}
        {(state.preview.details?.length ?? 0) > 0 && (
          <div className="mt-2">
            <IssueList
              title="后端说的具体原因"
              issues={state.preview.details ?? []}
              tone="error"
            />
          </div>
        )}
      </Card>

      {/* ============================================ 第 2 步：预览 */}
      {report !== null && issueGroups !== null && (
        <Card>
          <CardHeader
            title={commitReport !== null ? '③ 导入结果' : '② 预览结果（还没有写入任何数据）'}
            description={
              commitReport !== null
                ? '下面是这次真正写入的结果。'
                : `${describePreviewForConfirm(report)} 识别到的分隔符：${
                    report.parse.delimiter ?? '—'
                  }。`
            }
            action={
              report.parse.header.length > 0 ? (
                <Badge tone="neutral">识别到表头 {report.parse.header.join(' / ')}</Badge>
              ) : undefined
            }
          />

          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
            <Stat label="将新增" value={report.summary.created} tone="emerald" />
            <Stat label="将更新" value={report.summary.updated} tone="amber" />
            <Stat label="已存在跳过" value={report.summary.skipped} />
            <Stat label="有问题的行" value={report.summary.errors} tone="rose" />
            <Stat label="要新建课本" value={report.summary.textbooksToCreate} />
            <Stat label="要新建课文" value={report.summary.lessonsToCreate} />
          </div>

          {/*
            ⚠️ 那张**能自己加得起来的账**（用户核对的就是它）。
               拿不到时**明说**"账没拿到"，而不是渲染一堆 0（那会伪造一个"对得上"）。
          */}
          {report.summary.counts !== null ? (
            <ImportAccount
              counts={report.summary.counts}
              polyphonic={report.duplicates?.polyphonic ?? null}
            />
          ) : (
            <p className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              这次没拿到「可核对的账」（后端没返回 <code>summary.counts</code>）——
              这不是"你录了 0 个"。下面按条列出了每一行的去向，可以先那样核对。
            </p>
          )}

          <p className="mt-2 text-xs text-slate-500">
            共解析 {countLabel(report.summary.rows)} 行数据（原文 {countLabel(report.parse.totalLines)}{' '}
            行，其中空行 {countLabel(report.parse.blankLines)}、课文行{' '}
            {countLabel(report.parse.contextLines)}；生字 {countLabel(report.parse.charRows)} 条、
            词语 {countLabel(report.parse.wordRows)} 条）。
          </p>

          {/*
            ---------- 重复明细（哪几行、哪个字/词、会怎么处理） ----------
            ⚠️ 用户原话："如果有重复的字或词，但有提示，**几行，什么字或词**。"
              两个导入入口共用同一个组件、同一份后端数据（`report.duplicates`）。
            ⚠️ 它**不是错误**：重复只会合并 / 跳过，不拦任何一批。
          */}
          <DuplicateReport
            duplicates={report.duplicates}
            mergedCount={report.summary.counts?.merged ?? null}
            polyphonicCount={report.summary.counts?.mergedPolyphonic ?? null}
          />

          {describeTruncation(report, commitReport !== null ? 'commit' : 'preview') !== '' && (
            <p className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              {describeTruncation(report, commitReport !== null ? 'commit' : 'preview')}
            </p>
          )}

          {commitReport?.links != null && (
            <p className="mt-2 rounded-xl bg-slate-100 px-3 py-2 text-sm text-slate-700">
              写入的关联：字 ↔ 词 {countLabel(commitReport.links.wordHanzi)} 条、字 ↔ 课文{' '}
              {countLabel(commitReport.links.hanziLesson)} 条、词 ↔ 课文{' '}
              {countLabel(commitReport.links.wordLesson)} 条。
            </p>
          )}

          {/* ---------- 有问题的行（按行号，能滚） ---------- */}
          {/*
            ⚠️ 2026-10-21：标题从「这些行没有被导入」改成「有问题的行」——
               用户让「按书录入」那边删掉那句说明文案，两个面板共用同一套说法，
               逐行错误**一条都没少**（只是标题不再另起一个词）。
          */}
          {(issueGroups.errors.rowLevel.length > 0 ||
            issueGroups.errors.textLevel.length > 0 ||
            issueGroups.warnings.rowLevel.length > 0 ||
            issueGroups.warnings.textLevel.length > 0) && (
            <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
              <IssueList
                title="有问题的行"
                issues={[...issueGroups.errors.textLevel, ...issueGroups.errors.rowLevel]}
                tone="error"
              />
              <IssueList
                title="需要注意（这些行会被导入）"
                issues={[...issueGroups.warnings.textLevel, ...issueGroups.warnings.rowLevel]}
                tone="warn"
              />
            </div>
          )}

          {/* ---------- 逐行明细（折叠，太多了默认不铺开） ---------- */}
          {report.created !== null && report.created.length > 0 && (
            <details className="mt-4">
              <summary className="cursor-pointer text-sm font-medium text-slate-700">
                将新增的 {report.created.length} 条（点开看逐行）
              </summary>
              <ul className="mt-2 max-h-64 space-y-1 overflow-y-auto pr-1 text-sm">
                {report.created.map((row, index) => (
                  <li key={`${row.line}-${index}`} className="flex min-w-0 gap-2 rounded-lg bg-slate-50 px-2 py-1">
                    <span className="shrink-0 tabular-nums text-slate-400">第 {row.line} 行</span>
                    <span className="shrink-0 rounded bg-white px-1 text-xs text-slate-500">
                      {row.kind === 'char' ? '字' : '词'}
                    </span>
                    <span className="min-w-0 flex-1 truncate font-medium text-slate-800">{row.text}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}

          {report.updated !== null && report.updated.length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer text-sm font-medium text-slate-700">
                将更新的 {report.updated.length} 条
              </summary>
              <ul className="mt-2 max-h-64 space-y-1 overflow-y-auto pr-1 text-sm">
                {report.updated.map((row, index) => (
                  <li key={`${row.line}-${index}`} className="flex min-w-0 gap-2 rounded-lg bg-slate-50 px-2 py-1">
                    <span className="shrink-0 tabular-nums text-slate-400">第 {row.line} 行</span>
                    <span className="min-w-0 flex-1 truncate font-medium text-slate-800">{row.text}</span>
                    <span className="min-w-0 flex-1 truncate text-xs text-slate-500">
                      {row.reason ?? ''}
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          )}

          {report.skipped !== null && report.skipped.length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer text-sm font-medium text-slate-700">
                已存在、跳过的 {report.skipped.length} 条
              </summary>
              <ul className="mt-2 max-h-64 space-y-1 overflow-y-auto pr-1 text-sm">
                {report.skipped.map((row, index) => (
                  <li key={`${row.line}-${index}`} className="flex min-w-0 gap-2 rounded-lg bg-slate-50 px-2 py-1">
                    <span className="shrink-0 tabular-nums text-slate-400">第 {row.line} 行</span>
                    <span className="min-w-0 flex-1 truncate font-medium text-slate-800">{row.text}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </Card>
      )}

      {/* ============================================ 第 3 步：提交 */}
      {previewReport !== null && commitReport === null && (
        <Card>
          <CardHeader
            title="③ 确认导入"
            description={describePreviewForConfirm(previewReport)}
          />
          {/*
            ⚠️ 这里是"预览与提交必须是同一份输入"的界面落点：
               输入一改，`gate.allowed` 就是 false，并且**说明为什么** ——
               一个灰掉又不说话的按钮，家长只会反复点。
          */}
          {!gate.allowed && (
            <p className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              {gate.reason}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <Button
              loading={state.commit.pending}
              disabled={!gate.allowed}
              onClick={onCommit}
              icon={<IconCheck size={18} />}
            >
              {dirty ? '输入已改动，请先重新预览' : `确认导入这 ${countLabel(previewReport.summary.rows)} 行`}
            </Button>
            <span className="text-xs text-slate-500">
              一次最多写入 {IMPORT_MAX_ROWS} 行；有问题的行不会写入，其余照常导入。
            </span>
          </div>
          {state.commit.error !== null && (
            <p className="mt-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              导入失败：{state.commit.error}
            </p>
          )}
          {(state.commit.details?.length ?? 0) > 0 && (
            <div className="mt-2">
              <IssueList
                title="后端说的具体原因"
                issues={state.commit.details ?? []}
                tone="error"
              />
            </div>
          )}
        </Card>
      )}

      {commitReport !== null && (
        <Card>
          <CardHeader
            title="导入完成"
            description={`这次真正写入了 ${countLabel(commitReport.summary.created)} 条新增、${countLabel(
              commitReport.summary.updated,
            )} 条更新，跳过 ${countLabel(commitReport.summary.skipped)} 条。`}
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={onReset}>
              导入下一批
            </Button>
            <span className="text-xs text-slate-500">
              想接着往里加内容，先点上面的「填回上次导入的内容」。
            </span>
          </div>
        </Card>
      )}
    </div>
  );
}
